/**
 * Cart drawer "You May Also Like" — fetches recommendations on first open.
 *
 * The theme's <product-recommendations> (global.js) loads via
 * IntersectionObserver. That does not work here: the panel lives inside a
 * closed <details>, which is `display: none`, and an element that is never
 * rendered never intersects — so the observer would not fire, and opening the
 * accordion would not re-trigger it.
 *
 * This listens for the <details> `toggle` event instead and fetches once, the
 * first time it is opened. When the accordion is configured to start open, the
 * fetch runs immediately on connect.
 *
 * Adding a product from this list drops it from the list: it is now in the
 * cart, and the section filters cart contents out, so the next recommendation
 * moves up. The request asks for more products than are displayed precisely so
 * there is one waiting.
 *
 * That refresh is NOT driven by a `cartUpdate` subscription here. `cart-drawer`
 * already re-renders the whole of `#CartDrawer` on a cart change
 * (cart-drawer.js:194), which destroys this element and builds a new one — so
 * a subscription would fetch once, then be thrown away moments later. Instead
 * the open state is persisted across that re-render so the rebuilt accordion
 * comes back open and immediately loads fresh rows.
 */
class CartDrawerCrossSell extends HTMLElement {
  /**
   * Whether the accordion was open before the drawer last re-rendered. Static
   * so it outlives any single instance — see connectedCallback.
   */
  static wasOpen = false;

  constructor() {
    super();
    this.loaded = false;
    this.onToggle = this.onToggle.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  connectedCallback() {
    this.details = this.closest("details");

    // Delegated, so it covers rows that do not exist yet.
    this.addEventListener("submit", this.onSubmit);

    // The drawer re-renders wholesale on a cart change, so this element is
    // rebuilt from the server's markup — which always starts closed. Restoring
    // the remembered state keeps the accordion open across an add, so the
    // customer sees the list update rather than it collapsing under them.
    if (this.details && CartDrawerCrossSell.wasOpen) {
      this.details.open = true;
    }

    if (!this.details) {
      this.load();
      return;
    }

    // Attached unconditionally: the listener records the open state as well as
    // triggering the load, so it is needed even when the panel starts open.
    this.details.addEventListener("toggle", this.onToggle);

    if (this.details.open) this.load();
  }

  disconnectedCallback() {
    this.details?.removeEventListener("toggle", this.onToggle);
    this.removeEventListener("submit", this.onSubmit);
  }

  /**
   * Adds to cart on behalf of the rows.
   *
   * The rows come from `quick-add-button`, which wraps simple products in the
   * theme's <product-form>. That component does its setup in its CONSTRUCTOR
   * (product-form.js:5) — and for an element created by innerHTML the
   * constructor runs BEFORE its children are parsed, so `querySelector("form")`
   * returns null and no submit listener is ever attached. It works on the rest
   * of the site only because those forms are in the initial HTML.
   *
   * Rather than patch a shared vendor component, the submit is handled here:
   * the listener is delegated on this element, so it covers rows injected at
   * any point. Variant products are left alone — their control is a <quick-add>
   * button that opens the picker modal and posts nothing.
   */
  onSubmit(event) {
    const form = event.target.closest("form[data-type='add-to-cart-form']");
    if (!form) return;

    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    if (button?.getAttribute("aria-disabled") === "true" || button?.disabled) return;

    button?.setAttribute("aria-disabled", "true");
    button?.classList.add("loading");

    const body = new FormData(form);

    fetch(`${window.routes.cart_add_url}`, {
      method: "POST",
      headers: { Accept: "application/javascript", "X-Requested-With": "XMLHttpRequest" },
      body,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) {
          console.error("[cart cross-sell] add to cart failed:", data.description || data.message);
          return;
        }

        // `cartUpdate` is what other components listen to (the icon bubble,
        // the PDP), but it does NOT redraw the drawer — cart-drawer.js
        // subscribes to it only to manage focus. The redraw is driven by the
        // `cart-drawer:refresh` DOM event (cart-drawer.js:184), so both are
        // fired: the pub/sub for everything else, the DOM event for the drawer.
        if (typeof publish === "function") {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "cart-drawer-cross-sell",
            productVariantId: body.get("id"),
            cartData: data,
          });
        }

        // This element is destroyed by the redraw; `wasOpen` brings the
        // rebuilt one back open so the customer sees the list update.
        document.dispatchEvent(new CustomEvent("cart-drawer:refresh", { bubbles: true }));
      })
      .catch((e) => console.error("[cart cross-sell]", e))
      .finally(() => {
        button?.removeAttribute("aria-disabled");
        button?.classList.remove("loading");
      });
  }

  onToggle() {
    // Static, not per-instance: the point is to survive this element being
    // destroyed and replaced by the drawer's re-render.
    CartDrawerCrossSell.wasOpen = this.details.open;
    if (this.details.open) this.load();
  }

  async load() {
    if (this.loaded) return;
    this.loaded = true; // set before awaiting, so a rapid re-open cannot double-fetch

    const url = this.dataset.url;
    if (!url) return;

    this.setAttribute("aria-busy", "true");

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Recommendations failed: ${response.status}`);

      const text = await response.text();
      const html = document.createElement("div");
      html.innerHTML = text;

      // The Section Rendering API returns the whole section; the rows are
      // inside its <product-recommendations> wrapper.
      const source = html.querySelector("product-recommendations");

      if (!source) {
        console.warn(
          "[cart cross-sell] no <product-recommendations> in the response — " +
            "the section did not render. URL:",
          url
        );
        return;
      }

      if (!source.innerHTML.trim().length) {
        console.warn(
          "[cart cross-sell] section rendered but produced no rows. " +
            "Both the recommendations API and the fallback collection came " +
            "back empty (or every product is already in the cart). " +
            "Fallback used:",
          source.dataset.fallback === "true",
          "| URL:",
          url
        );
        // Clear rather than return: on a refresh the previous rows are stale,
        // and leaving them would keep showing a product that is now in the
        // cart. The CSS collapses the panel when this is empty.
        this.innerHTML = "";
        return;
      }

      this.innerHTML = source.innerHTML;
    } catch (e) {
      console.error(e);
      // Leave the panel empty rather than showing a broken state; the CSS
      // collapses it when there is nothing to show.
      this.loaded = false;
    } finally {
      this.removeAttribute("aria-busy");
    }
  }
}

customElements.define("cart-drawer-cross-sell", CartDrawerCrossSell);
